import { useDispatchLocationAlarm } from '@hakimo-ui/hakimo/data-access';
import {
  EvidenceUploaderOption,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { toast } from '@hakimo-ui/hakimo/util';
import { Button } from '@hakimo-ui/shared/ui-base';
import { useRef, useState } from 'react';
import { useAlarmGroupStatic } from '../../unified-alarm-group/alarm-provider/AlarmGroupProvider';
import { EditableTimestampHandle } from '../../shared/EditableTimestamp';
import { type ResponderType, type SubCategory } from '../util';
import { CategoryToggle } from './CategoryToggle';
import { CommentBox } from './CommentBox';
import { ResponderToggle } from './ResponderToggle';
import { TimestampField } from './TimestampField';
import { badge, buildDispatchPayload, renderFields } from './util';

export function DispatchFields({
  locationAlarmId,
  product,
  timezone,
  onUploadSuccess,
  dataVersion: dataVersionProp,
  defaultResponder,
}: EvidenceUploaderOption & { defaultResponder?: ResponderType }) {
  const [subCategory, setSubCategory] = useState<SubCategory>('ACCEPTED');
  const [responder, setResponder] = useState(defaultResponder ?? 'POLICE');
  const [responderTouched, setResponderTouched] = useState(!!defaultResponder);
  const [notes, setNotes] = useState('');
  const timestampRef = useRef<EditableTimestampHandle>(null);
  const [tsTouched, setTsTouched] = useState(false);

  const { dataVersion: contextDataVersion } = useAlarmGroupStatic();
  const dataVersion = dataVersionProp ?? contextDataVersion;
  const { mutate, isLoading } = useDispatchLocationAlarm(locationAlarmId, {
    product,
    dataVersion:
      product === UnifiedMonitoringProduct.SCAN ? undefined : dataVersion,
    onSuccess: () => {
      toast('Successfully logged!', { type: 'success' });
      onUploadSuccess?.();
    },
    onError: () => toast('Failed to log the entry!', { type: 'error' }),
  });

  return (
    <div className="flex flex-col gap-2.5">
      <CategoryToggle value={subCategory} onChange={setSubCategory} />
      {renderFields([
        (n) => (
          <ResponderToggle
            badge={badge(n, responderTouched)}
            value={responder}
            onChange={(v) => {
              setResponder(v);
              setResponderTouched(true);
            }}
          />
        ),
        (n) => (
          <TimestampField
            badge={badge(n, tsTouched)}
            timezone={timezone}
            timestampRef={timestampRef}
            onTouched={() => setTsTouched(true)}
          />
        ),
        (n) => (
          <CommentBox
            badge={badge(n, notes.trim().length > 0)}
            value={notes}
            onChange={setNotes}
          />
        ),
      ])}
      <Button
        variant="primary"
        block
        onClick={() =>
          mutate(
            buildDispatchPayload(
              'dispatch',
              locationAlarmId,
              responder,
              notes,
              timestampRef.current?.getTimestamp() ?? Date.now(),
              subCategory
            )
          )
        }
        loading={isLoading}
        disabled={isLoading}
      >
        Submit
      </Button>
    </div>
  );
}
